/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React from 'react';
import {
  TicketListRow,
  type TicketListRowTrailingPill,
} from '../../../shared/ticket/TicketListRow';
import type { CashSessionRow } from '../../infra/cashSessionsRepo';
import { formatLocalTime } from '@shared/vueltoDateTime';
import { cashArqueoCopy } from './cashArqueoCopy';
import { formatMoney } from './cashManagement.utils';

function HistoryCompactRow({
  s,
  onPress,
  operatorLabel,
  group,
}: {
  s: CashSessionRow;
  onPress: () => void;
  operatorLabel?: (userId: string | null | undefined) => string | null;
  group?: 'solo' | 'grouped' | 'groupedLast';
}) {
  const isOpen = s.status === 'open';
  const timeStr = formatLocalTime(isOpen ? s.openedAt : s.closedAt);
  const diff = s.difference ?? 0;
  const operatorName = operatorLabel
    ? operatorLabel(isOpen ? s.openedBy : s.closedBy)
    : null;

  const title = isOpen ? `Turno abierto · desde ${timeStr}` : `Cierre ${timeStr}`;
  const meta = isOpen
    ? [
        operatorName ? `Por ${operatorName}` : null,
        `Debería haber ${formatMoney(s.expectedAmount ?? s.openingAmount ?? 0)}`,
      ]
        .filter(Boolean)
        .join(' · ')
    : [
        operatorName ? `Por ${operatorName}` : null,
        `Esperado ${formatMoney(s.expectedAmount ?? 0)}`,
        `Contado ${formatMoney(s.closingAmount ?? 0)}`,
      ]
        .filter(Boolean)
        .join(' · ');

  // Proto .lrow: q (diferencia con tono) + pill de estado rellena.
  const statusLabel = isOpen
    ? 'Abierta'
    : diff === 0
      ? cashArqueoCopy.noDifference
      : diff > 0
        ? 'Sobrante'
        : 'Faltante';
  const value = isOpen
    ? null
    : diff === 0
      ? cashArqueoCopy.noDifference
      : diff > 0
        ? `+${formatMoney(diff)}`
        : formatMoney(diff);
  const valueTone = isOpen ? null : diff < 0 ? 'bad' : 'ok';
  const trailingPills: TicketListRowTrailingPill[] | undefined = isOpen
    ? [{ label: 'Abierta', tone: 'brand' }]
    : diff === 0
      ? undefined
      : [{ label: statusLabel, tone: diff > 0 ? 'ok' : 'bad' }];

  return (
    <TicketListRow
      title={title}
      meta={meta}
      value={value}
      valueTone={valueTone}
      trailingPills={trailingPills}
      leadingIcon={
        isOpen
          ? { name: 'lock-open-outline', tone: 'brand' }
          : { name: 'receipt-outline', tone: 'muted' }
      }
      onPress={onPress}
      group={group}
      showChevron
      accessibilityLabel={
        isOpen
          ? `Turno abierto ${timeStr}`
          : `Cierre ${timeStr}, diferencia ${statusLabel}${value ? ` ${value}` : ''}`
      }
      testID={`caja.history.row.${s.id}`}
    />
  );
}

export function HistorySessionRow({
  s,
  onPress,
  useFlatTable: _useFlatTable,
  selected,
  operatorLabel,
  group,
}: {
  s: CashSessionRow;
  onPress: () => void;
  /** Legacy desktop table flag — ignorado. Desktop usa la misma lista Square que mobile. */
  useFlatTable?: boolean;
  selected?: boolean;
  operatorLabel?: (userId: string | null | undefined) => string | null;
  group?: 'solo' | 'grouped' | 'groupedLast';
}) {
  return (
    <HistoryCompactRow
      s={s}
      onPress={onPress}
      operatorLabel={operatorLabel}
      group={group}
    />
  );
}
